import { useAddPinnedProject, useRemovePinnedProject } from 'api/ProjectsAPI';
import {
  changeUserProject,
  fetchUserProjectRequest,
  selectFeatureFlags,
  selectProjects,
  selectUserProject,
} from 'core/userContext';
import { shouldNavigateBackToIndex } from 'core/userContext/utils';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BUTTON_SIZES, GhostButton } from 'ui/button';
import { Add16 } from 'ui/icons/action/Add16';
import { Search } from 'ui/icons/action/Search';
import { baseColors, H4 } from 'ui/theme';
import { sortObjectListByKey } from 'utils/arrayHelpers';
import { useModalContext } from 'utils/contexts/modal';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { ModalAction } from '../../../types';
import { NavSlider } from '../../NavSlider';
import {
  EmptyStateMessage,
  FlexContainer,
  ListContainer,
  ProjectsHeader,
  ProjectsHeading,
  ProjectsList,
  SearchInput,
} from './AccountsAndProjectsMenu.styles';
import { Footer } from './Footer';
import { Profile } from './Profile';
import { Projects } from './Projects';

export const TEXT = {
  PROJECTS: 'Projects',
  HEADER: 'Account and Projects',
  PROJECT_SEARCH_INPUT: 'project-search',
};

export const AccountsAndProjectsMenu = () => {
  const dispatch = useDispatch();
  const [debouncedQuery, projectQuery, setProjectQuery] = useDebouncedState('', 300);
  const { showModal } = useModalContext();

  const onSuccess = () => {
    dispatch(fetchUserProjectRequest());
  };

  const { mutate: pinProject } = useAddPinnedProject({ onSuccess });
  const { mutate: unpinProject } = useRemovePinnedProject({ onSuccess });

  const projects = useSelector(selectProjects);
  const userProject = useSelector(selectUserProject);
  const { PinnedProjects: shouldShowPinnedProjects } = useSelector(selectFeatureFlags);

  const selectedProjectId = userProject?.id;
  const shouldShowSearch = projects.length > 0;

  const currentProjects = useMemo(() => {
    const filtered = projects.filter(({ id, name }) =>
      `${name} ${id}`.toLowerCase().includes(debouncedQuery.toLowerCase()),
    );
    return sortObjectListByKey(filtered, 'name');
  }, [debouncedQuery, projects]);

  const handleChangeProject = (id: number) => {
    // clicking on the currently selected project is a noop
    if (id === selectedProjectId) return;

    // Handle digging out from routes that won't resolve
    // in another project, e.g. /templates/edit?templateId=2343
    if (shouldNavigateBackToIndex(window.location.pathname)) {
      showModal({ action: ModalAction.UNSAVED_CHANGES, payload: { projectId: id } });
    } else {
      dispatch(changeUserProject(id));
    }
  };

  return (
    <NavSlider headerContent={<H4>{TEXT.HEADER}</H4>}>
      <Profile />
      <ProjectsHeader data-test="profile-projects">
        <FlexContainer>
          <ProjectsHeading>{TEXT.PROJECTS}</ProjectsHeading>
          <GhostButton
            data-test="new-project-button"
            size={BUTTON_SIZES.SMALL}
            onClick={() => showModal({ action: ModalAction.NEW_PROJECT })}
          >
            <Add16 />
            <span>New project</span>
          </GhostButton>
        </FlexContainer>
        {shouldShowSearch && (
          <SearchInput
            autoComplete="off"
            autoFocus
            data-test={TEXT.PROJECT_SEARCH_INPUT}
            name={TEXT.PROJECT_SEARCH_INPUT}
            placeholder="Search for a project..."
            value={projectQuery}
            Prefix={<Search color={baseColors.secondaryText} />}
            onChange={setProjectQuery}
          />
        )}
      </ProjectsHeader>
      <ListContainer>
        <ProjectsList data-test="projects-list">
          <Projects
            currentProjectId={selectedProjectId}
            isPinnedProjectsEnabled={shouldShowPinnedProjects}
            onAddAction={(id) => pinProject({ pinnedProject: id })}
            onRemoveAction={(id) => unpinProject({ pinnedProject: id })}
            onChangeAction={handleChangeProject}
            projects={currentProjects}
            searchTerm={debouncedQuery}
          />
          {currentProjects.length === 0 && (
            <EmptyStateMessage>No matching projects</EmptyStateMessage>
          )}
        </ProjectsList>
      </ListContainer>
      <Footer />
    </NavSlider>
  );
};
